// One move of a café's players from another system (F-HQ-40, spec/14 §3.5): the stepper HQ follows from the export
// to the report.
//
//   Source        the venue and the platform (chosen when the move was started)
//   Upload        the final export, CSV or XLSX; the Clavian CSV's columns and a sample to download
//   Columns       which column of the file feeds each Clavian field, with what its first rows read as — the same
//                 readers the server imports with (`@clavian/domain`), so "1,234.50" is shown as ₹1,234.50 exactly
//   Time and GST  the old PC groups' station classes, the switch-over day, and whether GST was paid at top-up
//   Review        the dry run's totals, the findings, the large balances and HQ's ticks; Import, after a centred
//                 confirmation
//   Import        the batches, with their progress; resumed by opening the move again
//   Report        what the ledger holds now, the CSV report, and undo — of every unclaimed profile, or of one
//
// The step shown follows the move's state; a step already done can be opened again while the move can still change.

import type { MigrationField, MigrationRow, MigrationRun, UpdateMigrationMapping } from '@clavian/api-client';
import { CLAVIAN_CSV_COLUMNS, clavianCsvSample, parseAmountText, parseDateText, parseDurationText, parseEmailText, parsePhoneText } from '@clavian/domain/migration';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, PageHero, PageSkeleton, ProgressBar, RadioGroup, SelectField, StatCard, Stepper, Table, type TableColumn, TextField, VisuallyHidden } from '@clavian/ui-web';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { problemCode, problemDetail, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Loading } from '../../shell/layouts';
import { LoadFailure } from '../../shell/LoadFailure';
import { saveFile } from '../audit/save-file';
import { UploadDrop } from '../catalogue/UploadDrop';
import { NotPermitted, RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_ORGANIZATIONS } from '../sections';
import { organizationParams, RUN_TONE, usePlatformLabel } from './MigrationPage';
import {
  downloadMigrationReport,
  useImportMigration,
  useMigrationRows,
  useMigrationRun,
  useSendMovedEmail,
  useSetMigrationTicks,
  useUndoMigration,
  useUpdateMigrationMapping,
  useUploadMigrationFile,
} from './queries';

import './migration.css';

/** The Clavian fields, in the contract's order (`MIGRATION_FIELDS`; a test holds the two equal). */
export const MIGRATION_FIELD_ORDER: readonly MigrationField[] = [
  'sourceId',
  'username',
  'firstName',
  'lastName',
  'email',
  'phone',
  'dateOfBirth',
  'joinedOn',
  'lastVisitOn',
  'cashBalance',
  'bonusBalance',
  'timeBalance',
  'timePcGroups',
  'currency',
  'banned',
  'banReason',
  'note',
  'deleted',
];

const STEP_IDS = ['source', 'upload', 'columns', 'time', 'review', 'import', 'report'] as const;
type StepId = (typeof STEP_IDS)[number];

const MAX_FILE_BYTES = 15 * 1024 * 1024;
/** A select cannot hold an empty value: this one means "not in this file". */
const NONE = '-';

/** Where a move is, from its state: the furthest step it has reached. */
export function stepOf(run: MigrationRun): StepId {
  if (run.status === 'importing') return 'import';
  if (run.status === 'completed' || run.status === 'undone') return 'report';
  if (run.file === null) return 'upload';
  if (run.mapping === null) return 'columns';
  if (run.status === 'ready') return 'review';
  return 'time';
}

/** What changes a move's state: a new one moves the page on to where the move has got to. */
function stateKeyOf(run: MigrationRun): string {
  return `${run.status}:${run.file?.sha256 ?? ''}:${run.mapping === null ? 0 : 1}:${run.switchOn ?? ''}:${run.gstPaidAtTopUp ?? ''}`;
}

/** Today in India, as the default switch-over day. */
function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

/** The Clavian CSV sample, quoted where a cell needs it. */
function sampleCsv(): string {
  const cell = (value: string) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  return clavianCsvSample()
    .map((row) => row.map(cell).join(','))
    .join('\r\n')
    .concat('\r\n');
}

export function MigrationRunPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <RunView />
    </RequirePlatformPermission>
  );
}

function RunView() {
  const { t } = useTranslation('hq');
  const params = useParams({ strict: false }) as { organizationId?: string; migrationId?: string };
  const organizationId = params.organizationId ?? '';
  const migrationId = params.migrationId ?? '';
  const run = useMigrationRun(organizationId, migrationId);
  const platformLabel = usePlatformLabel();
  const reached = run.data === undefined ? 'source' : stepOf(run.data);
  // A new state moves the page on; a step reopened by hand (or the next one a button opened) stays open until the
  // move changes — the choice is kept with the state it was made in.
  const stateKey = run.data === undefined ? '' : stateKeyOf(run.data);
  const [choice, setChoice] = useState<{ readonly key: string; readonly step: StepId | undefined }>({ key: '', step: undefined });
  const shown = choice.key === stateKey ? choice.step : undefined;
  const setShown = (step: StepId | undefined) => setChoice({ key: stateKey, step });
  /** Open `step` once the move is as a step's save left it. */
  const openAfter = (step: StepId) => (next: MigrationRun) => setChoice({ key: stateKeyOf(next), step });

  const back = (
    <Link className="console-hq-back" to={HQ_PATHS.organizationMigration} params={organizationParams(organizationId)}>
      {t('migration.title')}
    </Link>
  );

  if (run.data === undefined) {
    if (run.isError && problemCode(run.error) === PROBLEM.forbidden) return <NotPermitted />;
    return (
      <div className="console-page">
        {back}
        {run.isError ? <LoadFailure message={t('migration.loadFailed')} error={run.error} onRetry={() => void run.refetch()} /> : <PageSkeleton />}
      </div>
    );
  }
  const data = run.data;
  const editable = data.status === 'draft' || data.status === 'ready';
  const reachedIndex = STEP_IDS.indexOf(reached);
  const step = shown ?? reached;
  const current = STEP_IDS.indexOf(step);

  return (
    <div className="console-page console-migration">
      {back}
      <PageHero
        tileName={data.venueName}
        title={data.venueName}
        meta={platformLabel(data.sourcePlatform)}
        status={<Badge tone={RUN_TONE[data.status]}>{t(`migration.status.${data.status}`)}</Badge>}
      />
      <Stepper
        className="console-migration__stepper"
        steps={STEP_IDS.map((id) => ({ id, label: t(`migration.steps.${id}`) }))}
        current={current}
        onStepSelect={(index) => {
          const id = STEP_IDS[index];
          // Back to a step already reached, while the move can still change (or the report and import once begun).
          if (id !== undefined && index <= reachedIndex && (editable || index >= STEP_IDS.indexOf('import'))) setShown(id);
        }}
      />
      {step === 'source' ? <SourceStep run={data} onNext={() => setShown(reachedIndex > 1 ? undefined : 'upload')} /> : null}
      {step === 'upload' ? <UploadStep organizationId={organizationId} run={data} onNext={() => setShown('columns')} /> : null}
      {step === 'columns' ? <ColumnsStep organizationId={organizationId} run={data} onNext={openAfter('time')} /> : null}
      {step === 'time' ? <TimeStep organizationId={organizationId} run={data} onNext={openAfter('review')} /> : null}
      {step === 'review' ? <ReviewStep organizationId={organizationId} run={data} /> : null}
      {step === 'import' ? <ImportStep organizationId={organizationId} run={data} /> : null}
      {step === 'report' ? <ReportStep organizationId={organizationId} run={data} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Source and upload
// ---------------------------------------------------------------------------------------------

function SourceStep({ run, onNext }: { readonly run: MigrationRun; readonly onNext: () => void }) {
  const { t } = useTranslation('hq');
  const platformLabel = usePlatformLabel();
  return (
    <section className="console-hq-panel" aria-labelledby="console-migration-source">
      <h2 id="console-migration-source">{t('migration.source.title')}</h2>
      <dl className="console-hq-terms">
        <dt>{t('migration.source.venue')}</dt>
        <dd>{run.venueName}</dd>
        <dt>{t('migration.source.platform')}</dt>
        <dd>{platformLabel(run.sourcePlatform)}</dd>
      </dl>
      <p className="console-muted">{t('migration.source.platformHint')}</p>
      <div className="console-form__actions">
        <Button onClick={onNext}>{t('migration.source.start')}</Button>
      </div>
    </section>
  );
}

/** The sentence for the code, then the server's own detail where it adds to it (which balances are not ticked, why a file cannot be read). */
function withDetail(error: unknown, text: string): string {
  const detail = problemDetail(error);
  return detail === undefined || detail === '' || text.includes(detail) ? text : `${text} ${detail}`;
}

function UploadStep({ organizationId, run, onNext }: { readonly organizationId: string; readonly run: MigrationRun; readonly onNext: () => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const format = useVenueFormatter();
  const upload = useUploadMigrationFile(organizationId, run.id);
  const [tooLarge, setTooLarge] = useState(false);
  const failure = upload.isError ? withDetail(upload.error, problems.text(upload.error, { fallback: t('migration.upload.tooLarge') })) : undefined;

  return (
    <div className="console-migration__grid">
      <section className="console-hq-panel" aria-labelledby="console-migration-upload">
        <h2 id="console-migration-upload">{t('migration.upload.title')}</h2>
        <UploadDrop
          label={t('migration.upload.label')}
          title={t('migration.upload.drop')}
          hint={t('migration.upload.hint')}
          accept=".csv,.txt,.tsv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={upload.isPending}
          fileName={run.file?.name ?? null}
          progress={upload.isPending ? 0.5 : null}
          progressLabel={t('migration.upload.reading')}
          onFile={(file) => {
            setTooLarge(false);
            if (file === null) return;
            if (file.size > MAX_FILE_BYTES) {
              setTooLarge(true);
              return;
            }
            upload.mutate(file);
          }}
        />
        {tooLarge ? (
          <p className="console-alert" role="alert">
            {t('migration.upload.tooLarge')}
          </p>
        ) : null}
        {failure !== undefined ? (
          <p className="console-alert" role="alert">
            {failure}
          </p>
        ) : null}
        {run.file !== null ? (
          <p className="console-migration__file">
            {t('migration.upload.read', { name: run.file.name, rows: format.number(run.file.rows), when: format.dateTime(run.file.uploadedAt, { date: 'medium', time: 'short' }) })}
          </p>
        ) : null}
        <div className="console-form__actions">
          <Button onClick={onNext} disabled={run.file === null || upload.isPending}>
            {t('migration.upload.continue')}
          </Button>
        </div>
      </section>
      <section className="console-hq-panel" aria-labelledby="console-migration-format">
        <h2 id="console-migration-format">{t('migration.upload.formatTitle')}</h2>
        <p className="console-muted">{t('migration.upload.formatHint')}</p>
        <Table
          className="console-migration__format"
          columns={[
            { key: 'column', header: t('migration.upload.columnHeader'), cell: (spec: (typeof CLAVIAN_CSV_COLUMNS)[number]) => <code>{spec.column}</code> },
            {
              key: 'meaning',
              header: t('migration.upload.meaningHeader'),
              cell: (spec) => (
                <span>
                  {t(`migration.upload.column.${spec.column}`)} {spec.required ? <Badge tone="accent">{t('migration.upload.required')}</Badge> : null}
                </span>
              ),
            },
          ]}
          rows={CLAVIAN_CSV_COLUMNS}
          rowKey={(spec) => spec.column}
          caption={t('migration.upload.formatTitle')}
          captionHidden
        />
        <div className="console-form__actions">
          <Button variant="secondary" onClick={() => saveFile('clavian-migration-sample.csv', sampleCsv(), 'text/csv;charset=utf-8')}>
            {t('migration.upload.sample')}
          </Button>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------------------------

type MappingBody = NonNullable<MigrationRun['mapping']>;

/** The body of a mapping change, keeping whatever was already set beyond what this step changes. */
function bodyOf(run: MigrationRun, change: Partial<UpdateMigrationMapping>): UpdateMigrationMapping {
  const mapping = (run.mapping ?? run.suggestedMapping ?? { columns: {}, moneyUnit: 'major', timeUnit: 'hms', dateOrder: 'dmy' }) as MappingBody;
  return {
    mapping,
    classMap: run.classMap ?? { groups: [], fallback: [] },
    gstPaidAtTopUp: run.gstPaidAtTopUp,
    switchOn: run.switchOn ?? todayLocal(),
    ...change,
  };
}

function useReadsAs(mapping: MappingBody): (field: MigrationField, text: string) => string | null {
  const format = useVenueFormatter();
  return (field, text) => {
    const value = text.trim();
    if (value === '') return '';
    switch (field) {
      case 'cashBalance':
      case 'bonusBalance': {
        const parsed = parseAmountText(value, { currency: 'INR', unit: mapping.moneyUnit });
        return parsed.kind === 'ok' ? format.money({ amount: parsed.minor, currency: 'INR' }) : null;
      }
      case 'timeBalance': {
        const parsed = parseDurationText(value, mapping.timeUnit);
        return parsed.kind === 'ok' ? format.duration(Math.abs(parsed.seconds)) : null;
      }
      case 'dateOfBirth':
      case 'joinedOn':
      case 'lastVisitOn': {
        const parsed = parseDateText(value, mapping.dateOrder, { allowSerial: true });
        return parsed.kind === 'ok' ? parsed.date : null;
      }
      case 'email': {
        const parsed = parseEmailText(value);
        return parsed.kind === 'ok' ? parsed.email : null;
      }
      case 'phone': {
        const parsed = parsePhoneText(value, 'IN');
        return parsed.kind === 'ok' ? parsed.e164 : null;
      }
      default:
        return value;
    }
  };
}

function ColumnsStep({ organizationId, run, onNext }: { readonly organizationId: string; readonly run: MigrationRun; readonly onNext: (next: MigrationRun) => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const update = useUpdateMigrationMapping(organizationId, run.id);
  const [mapping, setMapping] = useState<MappingBody>(() => (run.mapping ?? run.suggestedMapping ?? { columns: {}, moneyUnit: 'major', timeUnit: 'hms', dateOrder: 'dmy' }) as MappingBody);
  const readsAs = useReadsAs(mapping);
  const columnOptions = [{ value: NONE, label: t('migration.columns.notInFile') }, ...run.headers.map((header, index) => ({ value: String(index), label: header === '' ? `#${index + 1}` : header }))];
  const used = new Set(Object.values(mapping.columns).filter((index): index is number => index !== undefined));
  const unused = run.headers.filter((_, index) => !used.has(index));
  const usernameMissing = mapping.columns.username === undefined;

  const setColumn = (field: MigrationField, value: string) =>
    setMapping((previous) => {
      const columns: Record<string, number | undefined> = { ...previous.columns };
      if (value === NONE) delete columns[field];
      else columns[field] = Number(value);
      return { ...previous, columns };
    });

  const rows = MIGRATION_FIELD_ORDER.map((field) => ({ field }));
  const columns: TableColumn<{ field: MigrationField }>[] = [
    { key: 'field', header: t('migration.columns.field'), cell: ({ field }) => t(`migration.columns.fields.${field}`) },
    {
      key: 'column',
      header: t('migration.columns.fileColumn'),
      cell: ({ field }) => (
        <SelectField
          label={<VisuallyHidden>{t('migration.columns.fileColumn')}: {t(`migration.columns.fields.${field}`)}</VisuallyHidden>}
          options={columnOptions}
          value={mapping.columns[field] === undefined ? NONE : String(mapping.columns[field])}
          onValueChange={(value) => setColumn(field, value)}
        />
      ),
    },
    {
      key: 'reads',
      header: t('migration.columns.reads'),
      cell: ({ field }) => {
        const index = mapping.columns[field];
        if (index === undefined) return <span className="console-muted">—</span>;
        const samples = run.sampleRows.slice(0, 3).map((row) => row[index] ?? '');
        return (
          <span className="console-migration__samples">
            {samples.map((sample, position) => {
              const read = readsAs(field, sample);
              return (
                <span key={position} className={read === null ? 'console-migration__sample console-migration__sample--bad' : 'console-migration__sample'}>
                  {sample === '' ? '—' : read === null ? `${sample} · ${t('migration.columns.unreadable')}` : sample === read ? sample : `${sample} → ${read}`}
                </span>
              );
            })}
          </span>
        );
      },
    },
  ];

  return (
    <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-columns">
      <h2 id="console-migration-columns">{t('migration.columns.title')}</h2>
      <p className="console-muted">{t('migration.columns.hint')}</p>
      <div className="console-migration__units">
        <SelectField
          label={t('migration.columns.moneyUnit')}
          options={(['major', 'minor'] as const).map((value) => ({ value, label: t(`migration.columns.money.${value}`) }))}
          value={mapping.moneyUnit}
          onValueChange={(value) => setMapping((previous) => ({ ...previous, moneyUnit: value as MappingBody['moneyUnit'] }))}
        />
        <SelectField
          label={t('migration.columns.timeUnit')}
          options={(['hms', 'hours', 'minutes', 'seconds'] as const).map((value) => ({ value, label: t(`migration.columns.time.${value}`) }))}
          value={mapping.timeUnit}
          onValueChange={(value) => setMapping((previous) => ({ ...previous, timeUnit: value as MappingBody['timeUnit'] }))}
        />
        <SelectField
          label={t('migration.columns.dateOrder')}
          options={(['dmy', 'mdy', 'ymd'] as const).map((value) => ({ value, label: t(`migration.columns.date.${value}`) }))}
          value={mapping.dateOrder}
          onValueChange={(value) => setMapping((previous) => ({ ...previous, dateOrder: value as MappingBody['dateOrder'] }))}
        />
      </div>
      <Table className="console-migration__mapping" columns={columns} rows={rows} rowKey={(row) => row.field} caption={t('migration.columns.title')} captionHidden />
      {unused.length > 0 ? <p className="console-muted">{t('migration.columns.unmapped', { count: unused.length, names: unused.join(', ') })}</p> : null}
      {usernameMissing ? (
        <p className="console-alert" role="alert">
          {t('migration.columns.usernameRequired')}
        </p>
      ) : null}
      {update.isError ? (
        <p className="console-alert" role="alert">
          {withDetail(update.error, problems.text(update.error))}
        </p>
      ) : null}
      <div className="console-form__actions">
        <Button busy={update.isPending} disabled={usernameMissing} onClick={() => update.mutate(bodyOf(run, { mapping }), { onSuccess: onNext })}>
          {t('migration.columns.continue')}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Time and GST
// ---------------------------------------------------------------------------------------------

function ClassChoice({ run, label, value, onChange }: { readonly run: MigrationRun; readonly label: string; readonly value: readonly string[]; readonly onChange: (next: string[]) => void }) {
  return (
    <fieldset className="console-migration__classes">
      <legend>{label}</legend>
      {run.stationClasses.map((stationClass) => (
        <Checkbox
          key={stationClass.id}
          label={stationClass.name}
          checked={value.includes(stationClass.id)}
          onCheckedChange={(checked) => onChange(checked === true ? [...value, stationClass.id] : value.filter((id) => id !== stationClass.id))}
        />
      ))}
    </fieldset>
  );
}

function TimeStep({ organizationId, run, onNext }: { readonly organizationId: string; readonly run: MigrationRun; readonly onNext: (next: MigrationRun) => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const update = useUpdateMigrationMapping(organizationId, run.id);
  const initialGroups = useMemo(() => new Map((run.classMap?.groups ?? []).map((group) => [group.group.toLowerCase(), group.stationClassIds as string[]])), [run.classMap]);
  const [groups, setGroups] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(run.pcGroups.map((group) => [group, initialGroups.get(group.toLowerCase()) ?? run.stationClasses.filter((one) => one.name.toLowerCase() === group.toLowerCase()).map((one) => one.id)])),
  );
  const [fallback, setFallback] = useState<string[]>(() => (run.classMap?.fallback as string[] | undefined) ?? run.stationClasses.slice(0, 1).map((one) => one.id));
  const [gst, setGst] = useState<string | undefined>(run.gstPaidAtTopUp === null ? undefined : run.gstPaidAtTopUp ? 'yes' : 'no');
  const [switchOn, setSwitchOn] = useState(run.switchOn ?? todayLocal());

  const save = () =>
    update.mutate(
      bodyOf(run, {
        classMap: { groups: Object.entries(groups).map(([group, stationClassIds]) => ({ group, stationClassIds: stationClassIds as never[] })), fallback: fallback as never[] },
        // Asked only of a venue registered for GST on the switch-over day; for one that is not, there is no answer to send.
        gstPaidAtTopUp: !run.gstQuestion || gst === undefined ? null : gst === 'yes',
        switchOn,
      }),
      { onSuccess: onNext },
    );

  return (
    <div className="console-migration__grid">
      <section className="console-hq-panel" aria-labelledby="console-migration-groups">
        <h2 id="console-migration-groups">{t('migration.time.groupsTitle')}</h2>
        <p className="console-muted">{t('migration.time.groupsHint')}</p>
        {run.stationClasses.length === 0 ? <p className="console-alert">{t('migration.time.noClasses')}</p> : null}
        {run.pcGroups.length === 0 ? <p className="console-muted">{t('migration.time.noGroups')}</p> : null}
        {run.pcGroups.map((group) => (
          <ClassChoice key={group} run={run} label={`${t('migration.time.group')}: ${group}`} value={groups[group] ?? []} onChange={(next) => setGroups((previous) => ({ ...previous, [group]: next }))} />
        ))}
        <ClassChoice run={run} label={t('migration.time.fallback')} value={fallback} onChange={setFallback} />
      </section>
      <section className="console-hq-panel" aria-labelledby="console-migration-gst">
        <h2 id="console-migration-gst">{run.gstQuestion ? t('migration.time.gstTitle') : t('migration.time.switchTitle')}</h2>
        <TextField label={t('migration.time.switchOn')} description={t('migration.time.switchOnHint')} type="date" value={switchOn} onChange={(event) => setSwitchOn(event.target.value)} />
        {run.gstQuestion ? (
          <RadioGroup
            label={t('migration.time.gstQuestion')}
            description={t('migration.time.gstHint')}
            options={[
              { value: 'yes', label: t('migration.time.gstYes') },
              { value: 'no', label: t('migration.time.gstNo') },
            ]}
            value={gst}
            onValueChange={setGst}
          />
        ) : (
          <p className="console-migration__notice">{t('migration.time.noGst')}</p>
        )}
        {update.isError ? (
          <p className="console-alert" role="alert">
            {withDetail(update.error, problems.text(update.error))}
          </p>
        ) : null}
        <div className="console-form__actions">
          <Button busy={update.isPending} disabled={switchOn === ''} onClick={save}>
            {t('migration.time.continue')}
          </Button>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------------------------

function useIssueLabel() {
  const { t } = useTranslation('hq');
  return (code: string) => t(`migration.issue.${code}`, { defaultValue: code });
}

function RowsTable({ organizationId, run, status }: { readonly organizationId: string; readonly run: MigrationRun; readonly status: MigrationRow['status'] | undefined }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const issueLabel = useIssueLabel();
  const [limit, setLimit] = useState(25);
  const rows = useMigrationRows(organizationId, run.id, { ...(status === undefined ? {} : { status }), limit });
  const shown = (rows.data?.data ?? []).filter((row) => status !== undefined || row.issues.some((issue) => issue.severity !== 'info'));
  const columns: TableColumn<MigrationRow>[] = [
    { key: 'row', header: t('migration.review.column.row'), numeric: true, cell: (row) => format.number(row.rowNumber) },
    {
      key: 'player',
      header: t('migration.review.column.player'),
      cell: (row) => (
        <span className="console-migration__player">
          <strong>{row.username ?? row.sourceKey}</strong>
          {row.name === null ? null : <span className="console-muted">{row.name}</span>}
        </span>
      ),
    },
    { key: 'money', header: t('migration.review.column.money'), numeric: true, cell: (row) => format.money(row.cash) },
    { key: 'time', header: t('migration.review.column.time'), numeric: true, cell: (row) => (row.timeSeconds === 0 ? '—' : format.duration(Math.abs(row.timeSeconds))) },
    { key: 'status', header: t('migration.review.column.status'), cell: (row) => <Badge tone={row.status === 'error' ? 'danger' : row.status === 'warning' ? 'warn' : 'neutral'}>{t(`migration.rowStatus.${row.status}`)}</Badge> },
    {
      key: 'findings',
      header: t('migration.review.column.findings'),
      cell: (row) => (
        <span className="console-migration__findings">
          {row.issues
            .filter((issue) => issue.severity !== 'info')
            .map((issue) => (
              <Badge key={issue.code} tone={issue.severity === 'error' ? 'danger' : 'warn'}>
                {issueLabel(issue.code)}
              </Badge>
            ))}
        </span>
      ),
    },
  ];
  return (
    <>
      <Table columns={columns} rows={shown} rowKey={(row) => String(row.rowNumber)} caption={t('migration.review.rowsCaption')} captionHidden empty={rows.isPending ? <Loading /> : t('migration.review.noRows')} />
      {rows.data?.page.hasMore === true ? (
        <Button variant="ghost" onClick={() => setLimit((previous) => Math.min(100, previous + 25))} disabled={limit >= 100}>
          {t('migration.review.more')}
        </Button>
      ) : null}
    </>
  );
}

function ReviewStep({ organizationId, run }: { readonly organizationId: string; readonly run: MigrationRun }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const format = useVenueFormatter();
  const issueLabel = useIssueLabel();
  const ticks = useSetMigrationTicks(organizationId, run.id);
  const importing = useImportMigration(organizationId, run.id);
  const [filter, setFilter] = useState<'all' | 'error'>('all');
  const [confirming, setConfirming] = useState(false);
  const summary = run.summary;
  if (summary === null) return <Loading />;

  const ticked = new Set(run.ticked);
  const missingTicks = summary.largeBalances.filter((balance) => balance.needsTick && !ticked.has(balance.sourceKey)).length;
  const blocked = run.status !== 'ready' || missingTicks > 0 || (summary.cash.amount > 0 && !run.walletEnabled);
  const hours = format.duration(summary.timeSeconds);
  const issueEntries = Object.entries(summary.issueCounts).sort(([, a], [, b]) => b - a);

  return (
    <div className="console-migration__review">
      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-totals">
        <h2 id="console-migration-totals">{t('migration.review.totals')}</h2>
        <p className="console-migration__headline">
          {t(run.gstQuestion ? 'migration.review.headline' : 'migration.review.headlineNoGst', {
            players: format.number(summary.players),
            cash: format.money(summary.cash),
            taxPaid: format.money(summary.cashTaxPaid),
            hours,
            bonus: format.money(summary.bonusDropped),
          })}
        </p>
        <div className="console-migration__stats">
          <StatCard label={t('migration.review.players')} value={format.number(summary.players)} />
          <StatCard label={t('migration.review.cash')} value={format.money(summary.cash)} hint={summary.cashTaxPaid.amount > 0 ? `${t('migration.review.cashTaxPaid')}: ${format.money(summary.cashTaxPaid)}` : undefined} />
          <StatCard label={t('migration.review.time')} value={hours} />
          <StatCard label={t('migration.review.bonusDropped')} value={format.money(summary.bonusDropped)} hint={t('migration.review.bonusDroppedHint')} tone={summary.bonusDropped.amount > 0 ? 'warn' : 'default'} />
          <StatCard label={t('migration.review.owed')} value={format.money(summary.owedNotCarried)} hint={t('migration.review.owedHint')} />
          <StatCard label={t('migration.review.minors')} value={format.number(summary.minors)} />
          <StatCard label={t('migration.review.claimByEmail')} value={format.number(summary.claimableByEmail)} />
          <StatCard label={t('migration.review.counterOnly')} value={format.number(summary.counterOnly)} />
          <StatCard label={t('migration.review.existing')} value={format.number(summary.existingAccounts)} />
        </div>
      </section>

      <div className="console-migration__grid">
        <section className="console-hq-panel" aria-labelledby="console-migration-status">
          <h2 id="console-migration-status">{t('migration.review.statusTitle')}</h2>
          <dl className="console-hq-terms">
            {(['ok', 'warning', 'error', 'skipped'] as const).map((status) => (
              <FragmentRow key={status} term={t(`migration.review.byStatus.${status}`)} value={format.number(summary.byStatus[status])} />
            ))}
          </dl>
        </section>
        <section className="console-hq-panel" aria-labelledby="console-migration-issues">
          <h2 id="console-migration-issues">{t('migration.review.issuesTitle')}</h2>
          {issueEntries.length === 0 ? (
            <p className="console-muted">{t('migration.review.noIssues')}</p>
          ) : (
            <ul className="console-migration__issues">
              {issueEntries.map(([code, count]) => (
                <li key={code}>
                  <span>{issueLabel(code)}</span>
                  <span className="console-money">{format.number(count)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-large">
        <h2 id="console-migration-large">{t('migration.review.largeTitle')}</h2>
        <p className="console-muted">{t('migration.review.largeHint')}</p>
        <Table
          columns={[
            { key: 'player', header: t('migration.review.column.player'), cell: (balance: (typeof summary.largeBalances)[number]) => balance.username ?? balance.sourceKey },
            { key: 'money', header: t('migration.review.column.money'), numeric: true, cell: (balance) => format.money(balance.cash) },
            { key: 'time', header: t('migration.review.column.time'), numeric: true, cell: (balance) => (balance.timeSeconds === 0 ? '—' : format.duration(balance.timeSeconds)) },
            {
              key: 'tick',
              header: t('migration.review.tick'),
              cell: (balance) =>
                balance.needsTick ? (
                  <Checkbox
                    label={<VisuallyHidden>{t('migration.review.tickLabel', { player: balance.username ?? balance.sourceKey })}</VisuallyHidden>}
                    checked={ticked.has(balance.sourceKey)}
                    disabled={ticks.isPending}
                    onCheckedChange={(checked) => ticks.mutate(checked === true ? [...ticked, balance.sourceKey] : [...ticked].filter((key) => key !== balance.sourceKey))}
                  />
                ) : (
                  <span className="console-muted">—</span>
                ),
            },
          ]}
          rows={summary.largeBalances}
          rowKey={(balance) => balance.sourceKey}
          rowTone={(balance) => (balance.needsTick && !ticked.has(balance.sourceKey) ? 'warn' : undefined)}
          caption={t('migration.review.largeCaption')}
          captionHidden
          empty={t('migration.review.noLarge')}
        />
      </section>

      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-rows">
        <div className="console-migration__rows-head">
          <h2 id="console-migration-rows">{t('migration.review.rowsTitle')}</h2>
          <RadioGroup
            label={<VisuallyHidden>{t('migration.review.rowsTitle')}</VisuallyHidden>}
            orientation="horizontal"
            options={[
              { value: 'all', label: t('migration.review.filterAll') },
              { value: 'error', label: t('migration.review.filterErrors') },
            ]}
            value={filter}
            onValueChange={(value) => setFilter(value as 'all' | 'error')}
          />
        </div>
        <RowsTable organizationId={organizationId} run={run} status={filter === 'error' ? 'error' : undefined} />
      </section>

      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-go">
        <h2 id="console-migration-go">{t('migration.review.import')}</h2>
        {summary.cash.amount > 0 && !run.walletEnabled ? <p className="console-alert">{t('migration.review.walletOff')}</p> : null}
        {!run.gstQuestion ? <p className="console-migration__notice">{t('migration.review.noGst')}</p> : null}
        {missingTicks > 0 ? <p className="console-alert">{t('migration.review.ticksMissing', { count: missingTicks })}</p> : null}
        {run.status !== 'ready' ? <p className="console-alert">{run.gstQuestion && run.gstPaidAtTopUp === null && summary.cash.amount > 0 ? t('migration.review.gstUnanswered') : t('migration.review.notReady')}</p> : null}
        {ticks.isError ? (
          <p className="console-alert" role="alert">
            {withDetail(ticks.error, problems.text(ticks.error))}
          </p>
        ) : null}
        {importing.isError ? (
          <p className="console-alert" role="alert">
            {withDetail(importing.error, problems.text(importing.error, { fallback: t('migration.import.failed', { error: '' }) }))}
          </p>
        ) : null}
        <div className="console-form__actions">
          <Button disabled={blocked} onClick={() => setConfirming(true)}>
            {t('migration.review.import')}
          </Button>
        </div>
      </section>

      <Dialog
        open={confirming}
        onOpenChange={setConfirming}
        size="md"
        title={t('migration.review.confirmTitle')}
        description={t('migration.review.confirmBody', { players: format.number(summary.players), cash: format.money(summary.cash), hours, platform: t(`migration.platform.${run.sourcePlatform}`) })}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t('migration.review.cancel')}
            </Button>
            <Button
              onClick={() => {
                setConfirming(false);
                importing.mutate();
              }}
            >
              {t('migration.review.confirm')}
            </Button>
          </>
        }
      >
        <p className="console-migration__headline">
          {t('migration.review.headline', { players: format.number(summary.players), cash: format.money(summary.cash), taxPaid: format.money(summary.cashTaxPaid), hours, bonus: format.money(summary.bonusDropped) })}
        </p>
      </Dialog>
    </div>
  );
}

function FragmentRow({ term, value }: { readonly term: string; readonly value: string }) {
  return (
    <>
      <dt>{term}</dt>
      <dd className="console-money">{value}</dd>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Import and report
// ---------------------------------------------------------------------------------------------

function ImportStep({ organizationId, run }: { readonly organizationId: string; readonly run: MigrationRun }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const format = useVenueFormatter();
  const importing = useImportMigration(organizationId, run.id);
  const share = run.progress.toImport === 0 ? 0 : run.progress.imported / run.progress.toImport;
  return (
    <section className="console-hq-panel" aria-labelledby="console-migration-importing">
      <h2 id="console-migration-importing">{t('migration.import.title')}</h2>
      <ProgressBar value={share} label={t('migration.import.progress', { done: format.number(run.progress.imported), total: format.number(run.progress.toImport) })} />
      <p>{t('migration.import.progress', { done: format.number(run.progress.imported), total: format.number(run.progress.toImport) })}</p>
      <p className="console-muted">{t('migration.import.working')}</p>
      {run.lastError !== null && !importing.isPending ? (
        <p className="console-alert" role="alert">
          {t('migration.import.failed', { error: run.lastError })}
        </p>
      ) : null}
      {importing.isError ? (
        <p className="console-alert" role="alert">
          {withDetail(importing.error, problems.text(importing.error))}
        </p>
      ) : null}
      <div className="console-form__actions">
        <Button busy={importing.isPending} onClick={() => importing.mutate()}>
          {t('migration.import.resume')}
        </Button>
      </div>
    </section>
  );
}

function UndoDialog({
  open,
  onOpenChange,
  title,
  body,
  busy,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly body: string;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation('hq');
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={title}
      description={body}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('migration.undo.cancel')}
          </Button>
          <Button variant="danger" busy={busy} disabled={reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>
            {t('migration.undo.confirm')}
          </Button>
        </>
      }
    >
      <TextField label={t('migration.undo.reason')} description={t('migration.undo.reasonHint')} value={reason} onChange={(event) => setReason(event.target.value)} />
    </Dialog>
  );
}

function ReportStep({ organizationId, run }: { readonly organizationId: string; readonly run: MigrationRun }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const format = useVenueFormatter();
  const api = useApi();
  const undo = useUndoMigration(organizationId, run.id);
  const movedEmail = useSendMovedEmail(organizationId, run.id);
  const rows = useMigrationRows(organizationId, run.id, { status: 'imported', limit: 100 });
  const [undoing, setUndoing] = useState<{ readonly id?: string; readonly name?: string } | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const posted = run.posted;

  const confirmUndo = (reason: string) =>
    undo.mutate(
      { reason, ...(undoing?.id === undefined ? {} : { importedPlayerId: undoing.id }) },
      {
        onSuccess: (result) => {
          setUndoing(undefined);
          const kept = result.claimed.length > 0 ? ` ${t('migration.undo.claimedKept', { count: result.claimed.length, names: result.claimed.map((one) => one.username).join(', ') })}` : '';
          setNotice(`${t('migration.undo.done', { count: result.undone })}${kept}`);
        },
      },
    );

  return (
    <div className="console-migration__review">
      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-report">
        <h2 id="console-migration-report">{t('migration.report.title')}</h2>
        <p className="console-muted">{t('migration.report.lead')}</p>
        {posted === null ? null : (
          <div className="console-migration__stats">
            <StatCard label={t('migration.report.players')} value={format.number(Math.max(0, posted.players - posted.claimed))} />
            <StatCard label={t('migration.report.cash')} value={format.money(posted.cash)} hint={posted.cashTaxPaid.amount > 0 ? `${t('migration.report.cashTaxPaid')}: ${format.money(posted.cashTaxPaid)}` : undefined} />
            <StatCard label={t('migration.report.time')} value={format.duration(posted.timeSeconds)} />
            <StatCard label={t('migration.report.claimed')} value={format.number(posted.claimed)} />
            <StatCard label={t('migration.report.undone')} value={format.number(posted.undone)} />
          </div>
        )}
        {run.claimWindowEndsOn === null ? null : <p>{t('migration.report.windowEnds', { date: format.date(`${run.claimWindowEndsOn}T12:00:00.000Z`, 'long') })}</p>}
        {undo.isError ? (
          <p className="console-alert" role="alert">
            {withDetail(undo.error, problems.text(undo.error))}
          </p>
        ) : null}
        {notice === undefined ? null : (
          <p className="console-migration__notice" role="status">
            {notice}
          </p>
        )}
        {run.status === 'completed' ? (
          <div className="console-migration__moved-email">
            <h3>{t('migration.movedEmail.title')}</h3>
            <p className="console-muted">{t('migration.movedEmail.lead')}</p>
            <p role="status">
              {run.movedEmail.round === 0
                ? t('migration.movedEmail.notSent')
                : t('migration.movedEmail.sent', {
                    count: run.movedEmail.sent,
                    date: run.movedEmail.requestedAt === null ? '' : format.date(run.movedEmail.requestedAt, 'medium'),
                  })}
            </p>
            {movedEmail.isError ? (
              <p className="console-migration__notice" role="alert">
                {problemCode(movedEmail.error) === PROBLEM.otpDeliveryUnavailable ? t('migration.movedEmail.noProvider') : problems.text(movedEmail.error, { fallback: t('migration.movedEmail.failed') })}
              </p>
            ) : null}
            <div className="console-form__actions">
              <Button variant={run.movedEmail.round === 0 ? 'primary' : 'secondary'} busy={movedEmail.isPending} onClick={() => movedEmail.mutate(run.movedEmail.round > 0)}>
                {run.movedEmail.round === 0 ? t('migration.movedEmail.send') : t('migration.movedEmail.resend')}
              </Button>
            </div>
          </div>
        ) : null}
        <div className="console-form__actions">
          <Button
            variant="secondary"
            onClick={() => {
              void downloadMigrationReport(api, organizationId, run.id).then((csv) => saveFile(`clavian-migration-${run.id}.csv`, csv, 'text/csv;charset=utf-8'));
            }}
          >
            {t('migration.report.download')}
          </Button>
          {run.status === 'completed' ? (
            <Button variant="danger" onClick={() => setUndoing({})}>
              {t('migration.report.undoAll')}
            </Button>
          ) : null}
        </div>
      </section>

      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-records">
        <h2 id="console-migration-records">{t('migration.report.recordsTitle')}</h2>
        <Table
          columns={[
            { key: 'row', header: t('migration.review.column.row'), numeric: true, cell: (row: MigrationRow) => format.number(row.rowNumber) },
            { key: 'player', header: t('migration.review.column.player'), cell: (row) => row.username ?? row.sourceKey },
            { key: 'money', header: t('migration.review.column.money'), numeric: true, cell: (row) => format.money(row.cash.amount < 0 ? { amount: 0, currency: row.cash.currency } : row.cash) },
            { key: 'time', header: t('migration.review.column.time'), numeric: true, cell: (row) => (row.timeSeconds <= 0 ? '—' : format.duration(row.timeSeconds)) },
            {
              key: 'undo',
              header: <VisuallyHidden>{t('migration.report.undoOne')}</VisuallyHidden>,
              cell: (row) =>
                row.importedPlayerId === null ? null : (
                  <Button size="sm" variant="ghost" aria-label={t('migration.report.undoOneLabel', { player: row.username ?? row.sourceKey })} onClick={() => setUndoing({ id: row.importedPlayerId ?? undefined, name: row.username ?? row.sourceKey })}>
                    {t('migration.report.undoOne')}
                  </Button>
                ),
            },
          ]}
          rows={rows.data?.data ?? []}
          rowKey={(row) => String(row.rowNumber)}
          caption={t('migration.report.recordsCaption')}
          captionHidden
          empty={rows.isPending ? <Loading /> : t('migration.review.noRows')}
        />
      </section>

      <UndoDialog
        open={undoing !== undefined}
        onOpenChange={(open) => (open ? undefined : setUndoing(undefined))}
        title={undoing?.id === undefined ? t('migration.undo.allTitle') : t('migration.undo.oneTitle', { player: undoing.name ?? '' })}
        body={undoing?.id === undefined ? t('migration.undo.allBody') : t('migration.undo.oneBody')}
        busy={undo.isPending}
        onConfirm={confirmUndo}
      />
    </div>
  );
}

